
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { UserTable } from "@/components/admin/users/UserTable";
import { toast } from "sonner";
import { StatsCard } from "@/components/ui/StatsCard";
import { Users, UserPlus, CalendarDays } from "lucide-react";
import { Card } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const Dashboard = () => {
  const { data: owners, isLoading, refetch } = useQuery({
    queryKey: ["owners"],
    queryFn: async () => {
      const { data: ownerData, error: ownerError } = await supabase
        .from("profiles")
        .select(`
          *,
          owner_data (
            company_name,
            business_type_id
          )
        `)
        .eq("user_type", "owner")
        .eq("login_type", "owner")
        .order('updated_at', { ascending: false });

      if (ownerError) {
        console.error("Error fetching profiles:", ownerError);
        throw ownerError;
      }

      // Filtrer pour n'inclure que les propriétaires avec des données valides
      const validOwners = ownerData.filter(owner => owner.owner_data !== null);
      
      return validOwners;
    },
  });

  const { data: recentSignups, isLoading: isLoadingRecent } = useQuery({
    queryKey: ["recent-signups"],
    queryFn: async () => {
      const { data: signupData, error: signupError } = await supabase
        .from("profiles")
        .select(`
          *,
          owner_data (
            company_name
          )
        `)
        .eq("user_type", "owner")
        .eq("login_type", "owner")
        .order('created_at', { ascending: false })
        .limit(5);

      if (signupError) {
        console.error("Error fetching recent signups:", signupError);
        throw signupError;
      }

      // Filtrer pour n'inclure que les propriétaires avec des données valides
      const validSignups = signupData.filter(owner => owner.owner_data !== null);
      
      return validSignups;
    },
  });

  const handleBlockUser = async (userId: string, isBlocked: boolean) => {
    const { error } = await supabase
      .from("profiles")
      .update({ is_blocked: isBlocked })
      .eq("id", userId);

    if (error) {
      toast.error("Erreur lors de la mise à jour du statut de l'utilisateur");
      return;
    }

    toast.success(
      `Utilisateur ${isBlocked ? "bloqué" : "débloqué"} avec succès`
    );
    refetch();
  };

  const totalUsers = owners?.length || 0;
  const activeUsers = owners?.filter(user => !user.is_blocked).length || 0;
  const todaySignups = recentSignups?.filter(user => 
    new Date(user.created_at).toDateString() === new Date().toDateString()
  ).length || 0;

  return (
    <DashboardLayout userType="admin">
      <div className="container mx-auto p-6 space-y-6">
        {/* Statistiques */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Total Propriétaires"
            value={totalUsers.toString()}
            description="Nombre total de propriétaires"
            icon={Users}
          />
          <StatsCard
            title="Propriétaires Actifs"
            value={activeUsers.toString()}
            description="Propriétaires non bloqués"
            icon={UserPlus}
          />
          <StatsCard
            title="Inscriptions Aujourd'hui"
            value={todaySignups.toString()}
            description="Nouveaux propriétaires du jour"
            icon={CalendarDays}
          />
        </div>

        {/* Inscriptions récentes */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Inscriptions Récentes</h2>
          <div className="space-y-4">
            {isLoadingRecent ? (
              <p>Chargement des inscriptions récentes...</p>
            ) : (
              recentSignups?.map(user => (
                <div key={user.id} className="flex items-center justify-between border-b pb-3">
                  <div>
                    <p className="font-medium">{user.full_name}</p>
                    <p className="text-sm text-gray-500">
                      {user.owner_data?.company_name || "Entreprise non spécifiée"}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDistanceToNow(new Date(user.created_at), { 
                      addSuffix: true,
                      locale: fr 
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Liste des utilisateurs */}
        <h1 className="text-2xl font-bold">Gestion des propriétaires</h1>
        <UserTable
          owners={owners || []}
          isLoading={isLoading}
          onBlockUser={handleBlockUser}
        />
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
